import React, { useState } from "react";
import { useMonitor } from "../../contexts/MonitorContext";
import { Heading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { VisitStatus } from "@visitproof/types";
import moment from "moment";

const getStatusBadge = (status: VisitStatus) => {
  switch (status) {
    case "scheduled":
      return <Badge color="blue">Scheduled</Badge>;
    case "supervisor_enroute":
      return <Badge color="yellow">En Route</Badge>;
    case "supervisor_arrived":
      return <Badge color="orange">Arrived</Badge>;
    case "supervisor_started":
      return <Badge color="green">In Progress</Badge>;
    case "supervisor_ended":
    case "completed":
      return <Badge color="zinc">Completed</Badge>;
    case "canceled":
      return <Badge color="red">Canceled</Badge>;
    case "no_show":
      return <Badge color="red">No Show</Badge>;
    default:
      return <Badge color="zinc">{status}</Badge>;
  }
};

type TabType = "upcoming" | "today" | "past" | "all";

/**
 * Supervisor/Monitor visits page - shows their assigned visits
 */
const SupervisorVisits: React.FC = () => {
  const { visits, upcomingVisits, todayVisits, pastVisits, loading, error } = useMonitor();
  const [activeTab, setActiveTab] = useState<TabType>("upcoming");

  const getVisitsForTab = () => {
    switch (activeTab) {
      case "today":
        return todayVisits;
      case "upcoming":
        return upcomingVisits;
      case "past":
        return pastVisits;
      case "all":
        return visits;
      default:
        return visits;
    }
  };

  const displayedVisits = getVisitsForTab();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
        <Text className="text-red-600 dark:text-red-400">{error}</Text>
      </div>
    );
  }

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: "today", label: "Today", count: todayVisits.length },
    { key: "upcoming", label: "Upcoming", count: upcomingVisits.length },
    { key: "past", label: "Past", count: pastVisits.length },
    { key: "all", label: "All", count: visits.length },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Heading>My Visits</Heading>
        <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
          View and manage your scheduled visits
        </Text>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 mb-6 rounded-lg bg-zinc-100 dark:bg-zinc-800 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Visits Table */}
      {displayedVisits.length === 0 ? (
        <div className="p-8 text-center border rounded-lg border-zinc-200 dark:border-zinc-700">
          <Text className="text-zinc-500">No visits found</Text>
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Date</TableHeader>
              <TableHeader>Time</TableHeader>
              <TableHeader>Location</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedVisits.map((visit) => {
              const startTime = moment(visit.startDate.toDate());
              const endTime = moment(visit.endDate.toDate());
              return (
                <TableRow key={visit.id} href={`/visit/${visit.id}`}>
                  <TableCell className="font-medium">
                    {startTime.format("ddd, MMM D, YYYY")}
                  </TableCell>
                  <TableCell>
                    {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
                  </TableCell>
                  <TableCell>
                    {visit.location?.name || visit.location?.formattedAddress || "—"}
                  </TableCell>
                  <TableCell>{getStatusBadge(visit.status)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default SupervisorVisits;
